<template>
  <div class="consolidated-panel">
    <div class="agency-selector">
      <div class="agency-selector__header">
        <span class="agency-selector__title">Agency</span>
        <el-tag size="mini" type="success">Grouped deductions</el-tag>
      </div>
      <el-select
        v-model="agencyValue"
        :loading="agencyLoading"
        class="agency-select"
        filterable
        placeholder="Select agency">
        <el-option
          v-for="agency in agencyOptions"
          :key="agency.id"
          :label="formatAgencyLabel(agency)"
          :value="agency.id"/>
      </el-select>
      <div class="agency-details">
        <div class="agency-details__icon">
          <i class="el-icon-office-building"/>
        </div>
        <div>
          <span class="agency-details__name">{{ selectedAgencyDetails.name }}</span>
          <p>{{ selectedAgencyDetails.description }}</p>
        </div>
      </div>
      <div class="agency-tags">
        <el-tag
          v-for="tag in selectedAgencyDetails.tags"
          :key="tag"
          size="mini"
          type="info">
          {{ tag }}
        </el-tag>
      </div>
    </div>

    <div class="consolidated-table">
      <div class="generate-list">
        <div class="generate-list__header">
          <div>
            <span class="generate-list__title">What To Generate</span>
            <p>{{ selectedAgencyDetails.shortName }} consolidated remittance will include these deduction lines.</p>
          </div>
          <el-tag size="small">{{ selectedAgencyDeductions.length }} deductions</el-tag>
        </div>
        <el-table
          v-loading="deductionLoading"
          :data="selectedAgencyDeductions"
          size="small"
          stripe
          empty-text="No deductions configured for this agency"
          class="deduction-preview-table"
          style="width: 100%;">
          <el-table-column
            prop="code"
            label="Code"
            width="150"/>
          <el-table-column
            prop="description"
            label="Deduction"
            min-width="220"/>
          <el-table-column
            prop="share"
            label="Share"
            width="150">
            <template slot-scope="scope">
              <el-tag :type="scope.row.share === 'Employee + Government' ? 'success' : 'info'" size="mini">{{ scope.row.share }}</el-tag>
            </template>
          </el-table-column>
        </el-table>
        <p v-if="!selectedAgencyDeductions.length" class="generate-list__empty-note">
          No deductions were found for the selected agency.
        </p>
      </div>

      <el-table
        v-loading="loading"
        :data="report"
        stripe
        size="medium"
        element-loading-text="Loading..."
        empty-text="No posted ledgers available"
        class="remittance-table"
        style="width: 100%;">
        <el-table-column
          prop="year"
          label="Year"
          width="110"
          align="center"/>
        <el-table-column
          label="Month"
          min-width="150">
          <template slot-scope="scope">
            <span class="month-name">{{ formatMonth(scope.row.month) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          label="Agency"
          min-width="220">
          <template>
            <div class="agency-cell">
              <strong>{{ selectedAgencyDetails.shortName }}</strong>
              <span>{{ selectedAgencyDetails.name }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          label="Includes"
          min-width="240">
          <template>
            <span class="include-text">{{ selectedAgencyDeductions.length }} selected deduction lines for {{ selectedAgencyDetails.shortName }}</span>
          </template>
        </el-table-column>
        <el-table-column
          label="Generate"
          width="190"
          align="center">
          <template slot-scope="scope">
            <el-tooltip content="Generate consolidated agency remittance" placement="top">
              <el-button
                :disabled="!selectedAgency"
                type="primary"
                size="small"
                class="generate-button generate-button--wide"
                @click="$emit('download-consolidated', scope.row)">
                <i class="far fa-file-excel"/>
                Consolidate
              </el-button>
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>
      <div class="remittance-pagination">
        <el-pagination
          :page-size="meta.per_page"
          :pager-count="7"
          :total="meta.total"
          :current-page.sync="currentPage"
          layout="total, prev, pager, next"
          @current-change="$emit('page-change')"/>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ConsolidatedRemittanceTab',
  props: {
    agencyLoading: {
      default: false,
      type: Boolean
    },
    agencyOptions: {
      default: () => [],
      type: Array
    },
    deductionLoading: {
      default: false,
      type: Boolean
    },
    loading: {
      default: false,
      type: Boolean
    },
    meta: {
      default: () => ({ page: 1 }),
      type: Object
    },
    monthList: {
      default: () => [],
      type: Array
    },
    report: {
      default: () => [],
      type: Array
    },
    selectedAgency: {
      default: null,
      type: Number
    },
    selectedAgencyDeductions: {
      default: () => [],
      type: Array
    },
    selectedAgencyDetails: {
      default: () => ({
        name: 'Select agency',
        shortName: 'Select agency',
        description: 'Choose an agency to view its deductions.',
        tags: []
      }),
      type: Object
    }
  },
  computed: {
    agencyValue: {
      get() {
        return this.selectedAgency
      },
      set(value) {
        this.$emit('update-agency', value)
      }
    },
    currentPage: {
      get() {
        return this.meta.page
      },
      set(value) {
        this.$emit('update-page', value)
      }
    }
  },
  methods: {
    formatAgencyLabel(agency) {
      if (!agency) return ''
      return agency.shortName === agency.name ? agency.name : agency.shortName + ' - ' + agency.name
    },
    formatMonth(month) {
      return this.monthList[(month - 1)] || 'UNKNOWN'
    }
  }
}
</script>
